// AppLock integration — VISUAL mode with the deferred-encryption pattern.
//
// Rationale: this app stores user data in IndexedDB. Visual mode adds a lock
// screen for access protection WITHOUT the high-risk IndexedDB encryption
// migration (which the project retrospective documented as fragile). Users can
// enable a stronger tier later via the deferred pattern. See INTEGRATION.md
// gotcha #22: visual mode does NOT require storage migration.
import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { AppLock, type AppLockInstance } from '@ascend/applock';

const DEFERRED_KEY = 'app-lock-deferred';

export type LockDecision = 'pending' | 'skipped' | 'enabled';

// AppLock's meta-keys that must be cleared when transitioning states.
// These persist in localStorage and cause stale `isConfigured = true` if not removed.
const APPLOCK_META_KEYS = ['applock_hash', 'applock_salt', 'applock_config', 'applock_version'];

function clearAppLockMetaKeys() {
  for (const key of APPLOCK_META_KEYS) {
    localStorage.removeItem(key);
  }
}

function getStoredDecision(): LockDecision {
  const stored = localStorage.getItem(DEFERRED_KEY);
  if (stored === 'skipped') return 'skipped';
  if (stored === 'enabled') return 'enabled';
  return 'pending';
}

function createLock(): AppLockInstance {
  return AppLock.init({
    mode: 'visual',
    appRootSelector: '#root',
    lockOnHide: false,
    idleTimeout: 900,
    ui: false, // ALWAYS false — we render our own React screens
  });
}

interface LockState {
  lock: AppLockInstance | null;
  isLocked: boolean;
  isUnlocked: boolean;
  isConfigured: boolean;
  decision: LockDecision;
}

interface AppLockContextValue extends LockState {
  isEnabled: boolean;
  skipEncryption: () => void;
  enableEncryption: () => void;
}

const AppLockContext = createContext<AppLockContextValue | null>(null);

// CRITICAL: isConfigured must ALWAYS initialize as false.
// The real value comes from the lock instance after it's created.
// Initializing it to anything else (e.g. decision !== 'pending') causes
// the LockScreen to appear on first load instead of the SetupScreen.
function getInitialState(): LockState {
  const decision = getStoredDecision();
  return {
    lock: null,
    isLocked: false,
    isUnlocked: decision === 'skipped',
    isConfigured: false, // ALWAYS false — updated by lock instance
    decision,
  };
}

export function AppLockProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LockState>(getInitialState);
  const instanceRef = useRef<AppLockInstance | null>(null);

  // CRITICAL: Clear stale AppLock meta-keys when skipping.
  // Without this, a subsequent enableEncryption() would create a lock
  // that finds the old hash and reports isConfigured=true.
  const skipEncryption = useCallback(() => {
    localStorage.setItem(DEFERRED_KEY, 'skipped');
    clearAppLockMetaKeys();
    if (instanceRef.current) {
      instanceRef.current.destroy();
      instanceRef.current = null;
    }
    setState({
      lock: null,
      isLocked: false,
      isUnlocked: true,
      isConfigured: false,
      decision: 'skipped',
    });
  }, []);

  // CRITICAL: Clear stale AppLock meta-keys before enabling.
  // Old hashes from prior runs make the new lock instance think a password
  // was already set, showing LockScreen instead of SetupScreen.
  const enableEncryption = useCallback(() => {
    localStorage.setItem(DEFERRED_KEY, 'enabled');
    clearAppLockMetaKeys();
    setState((prev) => ({
      ...prev,
      decision: 'enabled',
      isUnlocked: false,
      isConfigured: false,
    }));
  }, []);

  useEffect(() => {
    if (state.decision !== 'enabled') return;

    const instance = createLock();
    instanceRef.current = instance;

    const onLock = () => {
      setState((prev) => ({ ...prev, isLocked: true, isUnlocked: false }));
    };
    const onUnlock = () => {
      setState((prev) => ({ ...prev, isLocked: false, isUnlocked: true, isConfigured: true }));
    };
    const onPasswordClear = () => {
      setState((prev) => ({ ...prev, isConfigured: false }));
    };
    const onSetup = () => {
      setState((prev) => ({ ...prev, isConfigured: true }));
    };

    instance.on('lock', onLock);
    instance.on('unlock', onUnlock);
    instance.on('password:clear', onPasswordClear);
    instance.on('setup', onSetup);

    // CRITICAL: The 'uninitialized' state means no password has been set.
    // Treat it as unlocked so the SetupScreen shows (not the LockScreen).
    setState((prev) => ({
      ...prev,
      lock: instance,
      isLocked: instance.isLocked,
      isUnlocked: instance.state === 'unlocked' || instance.state === 'uninitialized',
      isConfigured: instance.isConfigured,
    }));

    return () => {
      instance.destroy();
      instanceRef.current = null;
    };
  }, [state.decision]);

  // Loading state while lock instance is being created
  if (state.decision === 'enabled' && !state.lock) return null;

  return (
    <AppLockContext.Provider
      value={{
        ...state,
        isEnabled: state.decision === 'enabled' && state.lock !== null,
        skipEncryption,
        enableEncryption,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
}

export function useAppLock(): AppLockContextValue {
  const ctx = useContext(AppLockContext);
  if (!ctx) throw new Error('useAppLock must be used within AppLockProvider');
  return ctx;
}
